import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { api } from '@udm/contracts';
import { Crosshair, MapPin, Plus, RefreshCw, ScanSearch } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { adoptDevice, createDevice, fetchDeviceTypes, fetchDiscovered, locateDevice } from '../../api/client';
import { Card, Drawer, Field, PageHeader, ProtocolTag } from '../../ui/components';
import { formatNumber, formatRelative } from '../../ui/format';
import { ErrorNote } from '../admin/ErrorNote';
import { useAdminSites } from '../sites/sitesData';

type Discovered = api.DiscoveredDevice;
const KEY = ['admin', 'discovered'] as const;

/** Sites in tree order with indentation, active only. */
function useSiteOptions() {
  const sites = useAdminSites();
  return useMemo(() => {
    const items = (sites.data?.items ?? []).filter((s) => s.status === 'active');
    const byParent = new Map<string | null, api.Site[]>();
    const ids = new Set(items.map((s) => s.id));
    for (const s of items) {
      const k = s.parent_id && ids.has(s.parent_id) ? s.parent_id : null;
      byParent.set(k, [...(byParent.get(k) ?? []), s]);
    }
    const out: { id: string; label: string }[] = [];
    const walk = (p: string | null, depth: number) =>
      (byParent.get(p) ?? [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((s) => {
          out.push({ id: s.id, label: `${'  '.repeat(depth)}${s.name}` });
          walk(s.id, depth + 1);
        });
    walk(null, 0);
    return out;
  }, [sites.data]);
}

function AdoptionBadge({ d }: { d: Discovered }) {
  const { t } = useTranslation();
  const a = d.adoption;
  if (!a) return <span className="muted">—</span>;
  const tone =
    a.status === 'outside_areas' || a.status === 'no_position'
      ? 'warning'
      : a.status === 'locating' || a.status === 'waiting'
        ? 'info'
        : 'unknown';
  return (
    <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
      <span className={`badge badge-${tone}`} style={{ alignSelf: 'flex-start' }}>
        {t(`discovery.status.${a.status}`)}
      </span>
      <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
        {a.status === 'waiting' && a.due_at
          ? t('discovery.dueIn', { when: formatRelative(a.due_at) })
          : a.position
            ? `${formatNumber(a.position.lat, 5)}, ${formatNumber(a.position.lon, 5)}`
            : formatRelative(a.updated_at)}
      </span>
    </span>
  );
}

export function DiscoveredPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  /** The map on this device (reported position), its card open and ringed, with a way back to this list. */
  const showOnMap = (d: Discovered) => {
    const pos = d.adoption!.position!;
    const name = d.identifiers[0]?.value ?? d.name;
    void navigate(`/map?discovered=${encodeURIComponent(d.id)}`, {
      state: {
        highlight: {
          label: name,
          points: [{ id: d.id, name, lon: pos.lon, lat: pos.lat }],
          returnTo: { path: '/discovered', label: t('discovery.backToList') },
        },
      },
    });
  };
  const qc = useQueryClient();
  // Interim: a page-scoped refresh until discovery events are pushed over realtime/v1.
  const list = useQuery({ queryKey: KEY, queryFn: fetchDiscovered, refetchInterval: 15_000 });
  const [drawer, setDrawer] = useState<{ adopt: Discovered } | 'new' | null>(null);
  // Deep link from the map (?adopt=<device id>): open that device's adoption drawer once the list is loaded.
  const [params, setParams] = useSearchParams();
  const adoptId = params.get('adopt');
  useEffect(() => {
    const d = adoptId ? list.data?.items.find((x) => x.id === adoptId) : undefined;
    if (d) {
      setDrawer({ adopt: d });
      setParams({}, { replace: true });
    }
  }, [adoptId, list.data, setParams]);
  const [search, setSearch] = useState('');
  const locate = useMutation({
    mutationFn: (id: string) => locateDevice(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  const items = (list.data?.items ?? []).filter((d) => {
    const q = search.trim().toLowerCase();
    return (
      !q || d.name.toLowerCase().includes(q) || d.identifiers.some((i) => i.value.toLowerCase().includes(q))
    );
  });

  return (
    <div className="page">
      <PageHeader
        title={t('discovery.title')}
        subtitle={t('discovery.subtitle', { count: list.data?.items.length ?? 0 })}
        actions={
          <span className="toolbar">
            <button
              type="button"
              className="btn"
              onClick={() => void list.refetch()}
              disabled={list.isFetching}
            >
              <RefreshCw size={15} /> {t('discovery.refresh')}
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setDrawer('new')}>
              <Plus size={16} /> {t('discovery.add')}
            </button>
          </span>
        }
      />
      <div className={`with-drawer${drawer ? ' open' : ''}`}>
        <Card bodyClass="">
          <div className="card-body toolbar" style={{ borderBottom: '1px solid var(--color-border)' }}>
            <input
              className="input"
              style={{ flex: '1 1 240px' }}
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={t('common.search')}
            />
            <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
              {t('discovery.hint')}
            </span>
          </div>
          {list.isError ? (
            <div className="card-body">
              <ErrorNote error={list.error} />
            </div>
          ) : items.length === 0 && !list.isLoading ? (
            <div className="empty-state">
              <ScanSearch size={32} className="muted" />
              <span className="muted">{t('discovery.empty')}</span>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('discovery.identifier')}</th>
                    <th>{t('discovery.connection')}</th>
                    <th>{t('discovery.adoption')}</th>
                    <th className="hide-mobile">{t('discovery.discovered')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                          <strong className="mono toolbar">
                            {d.identifiers[0]?.value ?? d.name} <ProtocolTag protocol={d.protocol} />
                          </strong>
                          <span className="muted mono" style={{ fontSize: 'var(--fs-xs)' }}>
                            {d.identifiers.map((i) => i.scheme).join(', ')}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`status status-${d.online ? 'online' : 'offline'}`}>
                          <span className="dot" aria-hidden />
                          {d.online ? t('presence.online') : t('presence.offline')}
                        </span>
                        {d.gateway && (
                          <span className="muted" style={{ display: 'block', fontSize: 'var(--fs-xs)' }}>
                            {d.gateway.name}
                          </span>
                        )}
                      </td>
                      <td>
                        <AdoptionBadge d={d} />
                      </td>
                      <td className="muted hide-mobile">{formatRelative(d.discovered_at ?? undefined)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="toolbar" style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            disabled={!d.online || locate.isPending}
                            title={t('discovery.locate')}
                            onClick={() => locate.mutate(d.id)}
                          >
                            <Crosshair size={15} />{' '}
                            <span className="hide-mobile">{t('discovery.locate')}</span>
                          </button>
                          {d.adoption?.position && (
                            <button
                              type="button"
                              className="btn btn-ghost"
                              title={t('devices.detail.showOnMap')}
                              onClick={() => showOnMap(d)}
                            >
                              <MapPin size={15} />{' '}
                              <span className="hide-mobile">{t('devices.detail.showOnMap')}</span>
                            </button>
                          )}
                          <button type="button" className="btn" onClick={() => setDrawer({ adopt: d })}>
                            {t('discovery.adopt')}
                          </button>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {locate.error && (
            <div className="card-body">
              <ErrorNote error={locate.error} />
            </div>
          )}
        </Card>
        {drawer && (
          <DeviceDrawer
            key={drawer === 'new' ? 'new' : drawer.adopt.id}
            device={drawer === 'new' ? undefined : drawer.adopt}
            onClose={() => setDrawer(null)}
            onDone={async () => {
              await qc.invalidateQueries({ queryKey: KEY });
              await qc.invalidateQueries({ queryKey: ['admin', 'sites'] });
              setDrawer(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

function DeviceDrawer({
  device,
  onClose,
  onDone,
}: {
  device?: Discovered;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const sites = useSiteOptions();
  const types = useQuery({ queryKey: ['device-types'], queryFn: fetchDeviceTypes, staleTime: Infinity });
  const adopting = device !== undefined;
  const pos = device?.adoption?.position;
  const [form, setForm] = useState({
    site_id: '',
    name: device?.name ?? '',
    device_type: device?.device_type ?? 'other',
    scheme: 'serial',
    value: '',
    lat: pos ? String(pos.lat) : '',
    lon: pos ? String(pos.lon) : '',
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v: string) => (v.trim() === '' || !Number.isFinite(Number(v)) ? null : Number(v));
  const save = useMutation({
    mutationFn: () => {
      const coords = { lat: num(form.lat), lon: num(form.lon) };
      return adopting
        ? adoptDevice(device.id, {
            site_id: form.site_id,
            name: form.name,
            device_type: form.device_type,
            ...coords,
          })
        : createDevice({
            site_id: form.site_id,
            name: form.name,
            device_type: form.device_type,
            identifiers: [{ scheme: form.scheme, value: form.value }],
            ...coords,
          });
    },
    onSuccess: onDone,
  });
  useEffect(() => {
    if (!form.site_id && sites[0]) setForm((f) => ({ ...f, site_id: sites[0]!.id }));
  }, [sites, form.site_id]);
  const valid = Boolean(form.site_id && form.name.trim() && (adopting || (form.scheme && form.value.trim())));

  return (
    <Drawer
      title={
        adopting
          ? t('discovery.adoptTitle', { name: device.identifiers[0]?.value ?? device.name })
          : t('discovery.add')
      }
      subtitle={adopting ? t('discovery.adoptHint') : t('discovery.addHint')}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!valid || save.isPending}
            onClick={() => save.mutate()}
          >
            {adopting ? t('discovery.adopt') : t('common.create')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
        </>
      }
    >
      <Field label={t('discovery.site')}>
        <select className="input" value={form.site_id} onChange={(e) => set('site_id', e.target.value)}>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t('common.name')}>
        <input
          className="input"
          value={form.name}
          maxLength={120}
          onChange={(e) => set('name', e.target.value)}
        />
      </Field>
      <Field label={t('common.type')}>
        <select
          className="input"
          value={form.device_type}
          onChange={(e) => set('device_type', e.target.value)}
        >
          {(types.data?.items ?? []).map((ty) => (
            <option key={ty.key} value={ty.key}>
              {t(`catalog.deviceType.${ty.key}`, { defaultValue: ty.name_en })}
            </option>
          ))}
        </select>
      </Field>
      {!adopting && (
        <div className="grid" style={{ gridTemplateColumns: '1fr 2fr', gap: 'var(--space-3)' }}>
          <Field label={t('discovery.scheme')}>
            <input
              className="input mono"
              list="id-schemes"
              value={form.scheme}
              onChange={(e) => set('scheme', e.target.value.toLowerCase())}
            />
            <datalist id="id-schemes">
              {['serial', 'imei', 'iccid'].map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </Field>
          <Field label={t('discovery.identifier')}>
            <input className="input mono" value={form.value} onChange={(e) => set('value', e.target.value)} />
          </Field>
        </div>
      )}
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Field label={t('sites.latitude')}>
          <input
            className="input"
            inputMode="decimal"
            value={form.lat}
            onChange={(e) => set('lat', e.target.value)}
          />
        </Field>
        <Field label={t('sites.longitude')}>
          <input
            className="input"
            inputMode="decimal"
            value={form.lon}
            onChange={(e) => set('lon', e.target.value)}
          />
        </Field>
      </div>
      <ErrorNote error={save.error} />
    </Drawer>
  );
}
